import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Stripe Connect Express onboarding + status card for the photographer dashboard.
export default function ConnectCard() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/photographer/stripe/status");
      setStatus(data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const c = params.get("connect");
    if (c === "done") {
      toast.success("Stripe onboarding complete — checking your payout status…");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (c === "refresh") {
      toast.info("Onboarding link expired — click the button to continue where you left off.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/photographer/stripe/connect", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  const active = status?.onboarding_complete;
  const inProgress = status?.connected && !active;

  return (
    <div className="border border-white/10 p-6 bg-[#0a0a0a]" data-testid="connect-card">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h3 className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold">Automatic payouts</h3>
        {status && (
          <span
            className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${
              active ? "text-green-400 border-green-400/40" : inProgress ? "pm-orange border-[var(--pm-orange-40)]" : "text-white/40 border-white/20"
            }`}
            data-testid="connect-status-badge"
          >
            {active ? "Active" : inProgress ? "Onboarding incomplete" : "Not set up"}
          </span>
        )}
      </div>

      {error ? (
        <p className="text-xs text-red-400 font-condensed" data-testid="connect-error">{error}</p>
      ) : !status ? (
        <p className="text-xs text-white/40 font-condensed">Checking Stripe status…</p>
      ) : active ? (
        <p className="text-sm text-white/60 font-condensed" data-testid="connect-active-msg">
          Your Stripe account is connected. Your share of every sale is transferred to you automatically —
          Stripe pays it out to your bank on their standard schedule.
        </p>
      ) : (
        <>
          <p className="text-sm text-white/60 font-condensed mb-4">
            Connect a free Stripe Express account (~2 min) and your share of every sale is sent to your
            bank automatically — no more waiting for manual payouts.
          </p>
          <button
            onClick={startOnboarding}
            disabled={busy}
            className="bg-[var(--pm-orange)] text-black px-6 py-3 text-xs uppercase tracking-[0.3em] font-bold hover:bg-[var(--pm-orange-bright)] disabled:opacity-50"
            data-testid="connect-onboard-btn"
          >
            {busy ? "Redirecting…" : inProgress ? "Continue onboarding" : "Set up payouts"}
          </button>
        </>
      )}
    </div>
  );
}
